import type { ComponentProps, PropsWithChildren } from 'react';
import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type { PrismicLink } from 'corgi-types';
import type {
  GridColumnProps,
  GridRowProps,
  JustifyContentProps,
  MaxWidthProps,
  SpaceProps,
} from 'styled-system';
import {
  alignItems,
  display,
  gridColumn,
  gridRow,
  height,
  minHeight,
  minWidth,
  space,
  themeGet,
  width,
} from 'styled-system';

import { Flex } from 'common/components/Flex';
import type { IconName } from 'common/components/Icon';
import Icon from 'common/components/Icon';
import { Text } from 'common/components/Text';
import { breakpoint } from 'common/theme/helper';

interface TextWithIconProps
  extends
    GridColumnProps,
    GridRowProps,
    JustifyContentProps,
    SpaceProps,
    MaxWidthProps,
    PropsWithChildren {
  iconName: IconName;
  link?: PrismicLink;
  withSeparator?: boolean;
}

const iconSizes = ['16px', null, null, '20px'];

const UnstyledStyledGridItem = styled(Flex)<any>`
  ${display};
  ${gridColumn};
  ${gridRow};
  ${alignItems};

  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    -ms-grid-column: 1;
    max-width: ${({ maxWidth: mw }) => mw || '340px'};
  }

  ${breakpoint(2)} {
    ${({ withSeparator }) =>
      withSeparator &&
      css`
        &::after {
          content: '';
          height: 28px;
          border-left: 1px solid #bec4c4;
          margin-left: 24px;
        }
      `};
  }
`;

const StyledGridItem = (props: ComponentProps<typeof UnstyledStyledGridItem>) => (
  <UnstyledStyledGridItem
    display="flex"
    gridColumn={1}
    pr={[null, 'sp_24']}
    pt={['sp_20', null, null, 'sp_0']}
    alignItems="center"
    {...props}
  />
);

const UnstyledStyledText = styled(Text)<any>`
  ${props =>
    props.href &&
    css`
      &:active,
      &:hover {
        color: ${themeGet('colors.sys.primary.background.default')(props)};
      }
    `};
`;

const StyledText = (props: ComponentProps<typeof UnstyledStyledText>) => (
  <UnstyledStyledText
    color="sys.neutral.border.strong"
    fontSize={['fs_16', null, null, 'fs_18']}
    lineHeight={['lh_20', null, null, 'lh_24']}
    my="sp_0"
    {...props}
  />
);

const UnstyledStyledIcon = styled(Icon)<any>`
  ${height};
  ${minHeight};
  ${width};
  ${minWidth};
  ${space};
`;

const StyledIcon = (props: ComponentProps<typeof UnstyledStyledIcon>) => (
  <UnstyledStyledIcon
    height={iconSizes}
    minHeight={iconSizes}
    width={iconSizes}
    minWidth={iconSizes}
    mr={['sp_8', null, null, 'sp_12']}
    {...props}
  />
);

export const TextWithIcon = ({ iconName, link, children, ...props }: TextWithIconProps) => (
  <StyledGridItem {...props}>
    <StyledIcon name={iconName} />
    <StyledText
      {...(link
        ? {
            as: 'a',
            href: link.url,
            target: link.target,
          }
        : {})}
    >
      {children}
    </StyledText>
  </StyledGridItem>
);
